import { useContext, useEffect, useMemo, useState } from 'react';
import SubscriptionContext from '../../contexts/subscription/SubscriptionContext';
import CompanyContext from '../../contexts/company/CompanyContext';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { getCallsBySubscriptions } from '../../../domain/use_cases/calls/getCallsBySubscriptions';
import { getSmsBySubscriptions, SmsDoc } from '../../../domain/use_cases/sms/getSmsBySubscriptions';
import Call from '../../../domain/models/Call';
import dayjs from 'dayjs';

export interface LeedflowDailyStats {
    timestamp: number;
    smsSentCount: number;
    smsReceivedCount: number;
    callsOutboundCount: number;
    callsInboundCount: number;
    callsOutboundDurationSeconds: number;
    callsInboundDurationSeconds: number;
}

export interface CallsStats {
    totalCalls: number;
    inboundCount: number;
    outboundCount: number;
    totalSms: number;
    smsSentCount: number;
    smsReceivedCount: number;
    /** Total duration across all calls, in minutes */
    totalMinutes: number;
    averageDurationMinutes: number;
    totalCost: number;
    averageCost: number;
    callsBySubscription: Record<string, Call[]>;
    smsBySubscription: Record<string, SmsDoc[]>;
    dailyStats: LeedflowDailyStats[];
}

const CHART_DAYS = 60;

/**
 * Aggregates call statistics for every Leedflow subscription of the active
 * company for the current billing period.
 *
 * The period is read from `company.billing`, the Stripe mirror. If it is not resolved yet the
 * hook falls back to the first day of the current calendar month.
 */
export const useCallsStats = () => {
    const { subscriptions } = useContext(SubscriptionContext);
    const { activeCompany } = useContext(CompanyContext);
    const { authentication } = useContext(AuthenticationContext);
    const [callsBySubscription, setCallsBySubscription] = useState<Record<string, Call[]>>({});
    const [smsBySubscription, setSmsBySubscription] = useState<Record<string, SmsDoc[]>>({});
    const [loading, setLoading] = useState(false);

    // "Activité du mois" frames the CREDITS cycle, which is monthly whatever the billing
    // interval — the Stripe period covers a year on an annual plan. The backend resolves that
    // cycle and stores both its bounds on the subscription, as useCompanyCredits already reads.
    // Two fallbacks behind it: the Stripe mirror, which coincides with the cycle on a monthly
    // company, then the old `next_billing_date - 1 month` for the 17 companies that have no
    // Stripe customer and will never carry the mirror.
    const creditsCycle = useMemo(() => {
        const starts = (subscriptions ?? []).map((sub) => sub.credits_reset_at).filter((v): v is number => !!v);
        const ends = (subscriptions ?? []).map((sub) => sub.credits_period_end).filter((v): v is number => !!v);
        if (!starts.length || !ends.length) return null;
        return { start: Math.max(...starts), end: Math.min(...ends) };
    }, [subscriptions]);

    const billingStartMs: number | undefined = useMemo(() => {
        if (creditsCycle) return creditsCycle.start;

        const start = activeCompany?.billing?.period_start;
        if (start) return dayjs(start).valueOf();

        const billingDate = dayjs(activeCompany?.next_billing_date);
        return billingDate.isValid() ? billingDate.subtract(1, 'month').valueOf() : undefined;
    }, [creditsCycle, activeCompany?.billing?.period_start, activeCompany?.next_billing_date]);

    const billingEndMs: number | undefined = useMemo(() => {
        if (creditsCycle) return creditsCycle.end;

        const end = activeCompany?.billing?.period_end ?? activeCompany?.next_billing_date;
        return end ? dayjs(end).valueOf() : undefined;
    }, [creditsCycle, activeCompany?.billing?.period_end, activeCompany?.next_billing_date]);

    const chartStartMs = useMemo(() => dayjs().subtract(CHART_DAYS - 1, 'day').startOf('day').valueOf(), []);
    const chartEndMs = useMemo(() => dayjs().endOf('day').valueOf(), []);

    useEffect(() => {
        if (!subscriptions || subscriptions.length === 0 || !activeCompany?.api_key || !authentication?.token) {
            // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
            // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
            // external system") ; le setState synchrone est le reset qui precede l'appel.
            // eslint-disable-next-line react/set-state-in-effect
            setCallsBySubscription({});
            setSmsBySubscription({});
            return;
        }

        let cancelled = false;
        setLoading(true);

        const refs = subscriptions.map((sub) => ({
            id: sub.id,
            api_key: sub.api_key,
        }));

        const fetchStartMs = Math.min(billingStartMs ?? chartStartMs, chartStartMs);
        const fetchEndMs = Math.max(billingEndMs ?? chartEndMs, chartEndMs);

        Promise.all([
            getCallsBySubscriptions(
                {
                    subscriptions: refs,
                    startTimestamp: fetchStartMs,
                    endTimestamp: fetchEndMs,
                },
                activeCompany.api_key,
                authentication,
            ),
            getSmsBySubscriptions(
                {
                    subscriptions: refs,
                    startTimestamp: fetchStartMs,
                    endTimestamp: fetchEndMs,
                },
                activeCompany.api_key,
                authentication,
            ),
        ])
            .then(([callsResult, smsResult]) => {
                if (cancelled) return;
                setCallsBySubscription(callsResult);
                setSmsBySubscription(smsResult);
            })
            .catch((err) => {
                console.error('useCallsStats – error fetching calls/SMS stats:', err);
                if (!cancelled) {
                    setCallsBySubscription({});
                    setSmsBySubscription({});
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [subscriptions, billingStartMs, billingEndMs, chartStartMs, chartEndMs, activeCompany, authentication]);

    const callsStats: CallsStats = useMemo(() => {
        const allCalls = Object.values(callsBySubscription).flat();
        const allSms = Object.values(smsBySubscription).flat();

        const dayKeyOf = (timestampMs: number) => {
            const date = new Date(timestampMs);
            return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        };

        const dayBuckets = new Map<number, LeedflowDailyStats>();
        for (let i = 0; i < CHART_DAYS; i++) {
            const dayKey = dayKeyOf(dayjs(chartStartMs).add(i, 'day').valueOf());
            dayBuckets.set(dayKey, {
                timestamp: dayKey,
                smsSentCount: 0,
                smsReceivedCount: 0,
                callsOutboundCount: 0,
                callsInboundCount: 0,
                callsOutboundDurationSeconds: 0,
                callsInboundDurationSeconds: 0,
            });
        }

        for (const call of allCalls) {
            const stat = dayBuckets.get(dayKeyOf(call.created));
            if (!stat) continue; // outside the CHART_DAYS window
            if (call.direction === 'inbound') {
                stat.callsInboundCount += 1;
                stat.callsInboundDurationSeconds += call.duration;
            } else if (call.direction === 'outbound') {
                stat.callsOutboundCount += 1;
                stat.callsOutboundDurationSeconds += call.duration;
            }
        }

        for (const sms of allSms) {
            const stat = dayBuckets.get(dayKeyOf(sms.created));
            if (!stat) continue;
            if (sms.role === 'assistant') stat.smsSentCount += 1;
            else if (sms.role === 'user') stat.smsReceivedCount += 1;
        }

        const dailyStats: LeedflowDailyStats[] = Array.from(dayBuckets.values()).sort((a, b) => a.timestamp - b.timestamp);

        const inBilling = (ts: number) =>
            (billingStartMs == null || ts >= billingStartMs) && (billingEndMs == null || ts <= billingEndMs);
        const billingCalls = allCalls.filter((c) => inBilling(c.created));
        const billingSms = allSms.filter((s) => inBilling(s.created));

        const totalCalls = billingCalls.length;
        const totalSms = billingSms.length;
        const inboundCount = billingCalls.filter((c) => c.direction === 'inbound').length;
        const outboundCount = billingCalls.filter((c) => c.direction === 'outbound').length;

        const totalDurationSeconds = billingCalls.reduce((acc, c) => acc + c.duration, 0);
        const totalMinutes = parseFloat((totalDurationSeconds / 60).toFixed(2));
        const averageDurationMinutes = totalCalls > 0 ? parseFloat((totalMinutes / totalCalls).toFixed(2)) : 0;

        const totalCost = parseFloat(
            billingCalls.reduce((acc, c) => acc + c.price, 0).toFixed(2),
        );
        const averageCost = totalCalls > 0 ? parseFloat((totalCost / totalCalls).toFixed(2)) : 0;

        const smsSentCount = billingSms.filter((s) => s.role === 'assistant').length;
        const smsReceivedCount = billingSms.filter((s) => s.role === 'user').length;

        return {
            totalCalls,
            inboundCount,
            outboundCount,
            totalSms,
            smsSentCount,
            smsReceivedCount,
            totalMinutes,
            averageDurationMinutes,
            totalCost,
            averageCost,
            callsBySubscription,
            smsBySubscription,
            dailyStats,
        };
    }, [callsBySubscription, smsBySubscription, billingStartMs, billingEndMs, chartStartMs]);

    return {
        callsStats,
        loading,
    };
};
